import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Car, Users, FileText, FileStack,
  Megaphone, Settings, ChevronLeft, ChevronRight,
  Menu, X, LogOut, ChevronDown, ShoppingCart, BarChart2, Wrench,
  Package, Wallet, Truck, BookOpen, Cog, PenLine, BellRing, GripVertical, ChevronUp, Pencil, Check, RotateCcw, Sun, Moon,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import type { Permission } from '../../utils/permissions';
import BranchSwitcher from './BranchSwitcher';
import { loadAndApplyTheme } from '../../utils/theme';
import { useUiMode } from '../../hooks/useUiMode';
import { apiClient } from '../../api/client';
import { useAddons } from '../../lib/addons';
import SuperadminBanner from './SuperadminBanner';
import NoticeBanner from './NoticeBanner';
import TermsGate from '../common/TermsGate';
import NewsBell from '../common/NewsBell';
import { loadSharedSettings } from '../../utils/sharedSettings';
import { loadSellerSignature, useSellerSigState } from '../../utils/sellerSignature';
import MySignatureModal from '../esign/MySignatureModal';

interface NavItem { to: string; label: string; icon: typeof Car; perms?: Permission[] }
const BASE_NAV_ITEMS: NavItem[] = [
  { to: '/dashboard',   label: 'Dashboard',   icon: LayoutDashboard },
  { to: '/vehicles',    label: 'Vozidla',      icon: Car, perms: ['vehicles.view'] },
  { to: '/sales',       label: 'Prodej',       icon: ShoppingCart, perms: ['vehicles.sell'] },
  { to: '/vykup',       label: 'Výkup',        icon: Truck, perms: ['vehicles.create'] },
  { to: '/investments', label: 'Investice',    icon: Wrench, perms: ['investments.view'] },
  { to: '/customers',   label: 'Zákazníci',    icon: Users, perms: ['customers.view'] },
  { to: '/pokladna',    label: 'Pokladna',     icon: Wallet, perms: ['invoices.view', 'cashbook.manage'] },
  { to: '/advertising', label: 'Inzerce',      icon: Megaphone, perms: ['advertising.view'] },
  { to: '/statistics',  label: 'Statistiky',   icon: BarChart2, perms: ['statistics.view'] },
];

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Správce', MANAGER: 'Manažer', SALESPERSON: 'Prodejce',
  ACCOUNTANT: 'Účetní', VIEWER: 'Čtenář',
};

export default function AppShell({ children }: { children: React.ReactNode }) {
  const [collapsed,   setCollapsed]   = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [userMenuOpen,setUserMenuOpen]= useState(false);

  const addons = useAddons();
  const uiMode = useUiMode();
  const { canAny } = usePermissions();
  const NAV_ITEMS: NavItem[] = [
    ...BASE_NAV_ITEMS,
    ...(addons.inventory ? [{ to: '/inventory', label: 'Sklad', icon: Package, perms: ['inventory.view'] as Permission[] }] : []),
    ...(addons.service ? [{ to: '/service', label: 'Servis', icon: Cog, perms: ['service.view'] as Permission[] }] : []),
    ...(addons.aftersales ? [{ to: '/aftersales', label: 'Poprodejní služby', icon: BellRing, perms: ['aftersales.view'] as Permission[] }] : []),
    { to: '/settings', label: 'Nastavení', icon: Settings, perms: ['settings.view'] as Permission[] },
  ].filter(i => !i.perms || canAny(i.perms));

  // Pořadí hlavního menu si každý uživatel skládá po svém (ukládá se v tomto prohlížeči)
  const { user: navUser } = useAuthStore();
  const navKey = `nav_order_v1_${navUser?.id ?? 'x'}`;
  const [navOrder, setNavOrder] = useState<string[]>(() => {
    try { const v = JSON.parse(localStorage.getItem(navKey) ?? '[]'); return Array.isArray(v) ? v.filter((x: unknown) => typeof x === 'string') : []; } catch { return []; }
  });
  const [editNav, setEditNav] = useState(false);
  const [dragNav, setDragNav] = useState<string | null>(null);
  const orderedNav = useMemo(() => {
    const rest = new Map(NAV_ITEMS.map(i => [i.to, i]));
    const out: typeof NAV_ITEMS = [];
    for (const to of navOrder) { const it = rest.get(to); if (it) { out.push(it); rest.delete(to); } }
    for (const it of NAV_ITEMS) if (rest.has(it.to)) out.push(it);
    return out;
  }, [navOrder, addons.inventory, addons.service, addons.aftersales, NAV_ITEMS.map(i => i.to).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveNavOrder = (order: string[]) => {
    setNavOrder(order);
    try { localStorage.setItem(navKey, JSON.stringify(order)); } catch { /* plné úložiště */ }
  };
  const moveNav = (to: string, toIndex: number) => {
    const list = orderedNav.map(i => i.to);
    const from = list.indexOf(to); if (from < 0) return;
    list.splice(from, 1); list.splice(Math.max(0, Math.min(list.length, toIndex)), 0, to);
    saveNavOrder(list);
  };

  // Apply saved theme on mount
  useEffect(() => { loadAndApplyTheme(); }, []);

  // Sdílená nastavení (šablony smluv, číselníky, lhůty) se načtou jednou po
  // přihlášení. Generátory dokumentů je čtou synchronně, takže se čeká, než
  // dorazí — jinak by první vytištěná smlouva mohla použít starý lokální text.
  const [settingsReady, setSettingsReady] = useState(false);
  useEffect(() => {
    let alive = true;
    loadSharedSettings().finally(() => {
      if (!alive) return;
      loadAndApplyTheme();   // téma může být na serveru novější než lokální kopie
      setSettingsReady(true);
    });
    return () => { alive = false; };
  }, []);

  // Podpis prodejce (modul E-podpis): načte se ten, který smí tento uživatel použít, a drží se v paměti pro tisk smluv.
  const sigState = useSellerSigState();
  const [mySigOpen, setMySigOpen] = useState(false);
  useEffect(() => { if (addons.esign) loadSellerSignature(); }, [addons.esign]);

  const location = useLocation();
  const navigate  = useNavigate();
  const { user, logout, superadmin } = useAuthStore();

  const handleLogout = useCallback(() => {
    // Odhlašuje-li se superadmin, ukonči i sdílenou relaci (banner zmizí ostatním).
    if (superadmin) { apiClient.post('/auth/superadmin/stop').catch(() => {}); }
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate, superadmin]);

  // ── Idle / auto-logout timer ─────────────────────────────────────────────
  // Hodnota se čte z backendu (Nastavení → Bezpečnost, globální pro celou firmu).
  // 0 = automatické odhlášení vypnuto.
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTimeoutMinutes = useRef<number>(60);

  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    const minutes = sessionTimeoutMinutes.current;
    if (minutes > 0) {
      idleTimer.current = setTimeout(() => {
        handleLogout();
      }, minutes * 60 * 1000);
    }
  }, [handleLogout]);

  const loadSecuritySettings = useCallback(async () => {
    try {
      const res = await apiClient.get('/settings/security');
      sessionTimeoutMinutes.current = Number(res.data?.data?.sessionTimeoutMinutes ?? 60);
    } catch {
      sessionTimeoutMinutes.current = 60;
    }
    resetIdleTimer();
  }, [resetIdleTimer]);

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    window.addEventListener('autoLogoutSettingsChanged', loadSecuritySettings);
    loadSecuritySettings();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      window.removeEventListener('autoLogoutSettingsChanged', loadSecuritySettings);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [resetIdleTimer, loadSecuritySettings]);

  const initials = user?.fullName
    ? user.fullName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2)
    : 'A';

  if (!settingsReady) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-blue-600 animate-spin"/>
          Načítám nastavení…
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <SuperadminBanner />
      <NoticeBanner />
      <TermsGate />
      <div className="app-bg flex flex-1 min-h-0 bg-gray-50 overflow-hidden">
      <style>{`
        @media print {
          .app-sidebar { display: none !important; }
          header { display: none !important; }
          main { overflow: visible !important; height: auto !important; padding: 24px !important; }
          .flex-1.flex.flex-col { width: 100% !important; }
        }
      `}</style>
      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside className={`app-sidebar fixed lg:relative z-30 h-full bg-gray-900 text-white flex flex-col transition-all duration-200
        ${collapsed ? 'w-16' : 'w-60'}
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>

        {/* Logo */}
        <div className="flex items-center gap-3 px-4 h-16 border-b app-sidebar-border border-gray-700 shrink-0">
          <svg viewBox="0 0 40 40" className="w-8 h-8 shrink-0" xmlns="http://www.w3.org/2000/svg" aria-label="CarFlow4u">
            <rect width="40" height="40" rx="11" fill="#0f172a" />
            <path d="M7 30.5 L31 25.8 L31 28.1 L7 32 Z" fill="#38bdf8" />
            <text x="20" y="24" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="700" letterSpacing="-1.2">
              <tspan fill="#ffffff">cf</tspan><tspan fill="#38bdf8">4u</tspan>
            </text>
          </svg>
          {!collapsed && (
            <div className="overflow-hidden">
              <div className="font-bold text-sm">CarFlow4u</div>
              <div className="app-sidebar-text text-gray-400 text-xs">Systém pro autobazary</div>
            </div>
          )}
          {/* Oznámení o novinkách – zvonek, ne pruh přes dashboard */}
          <NewsBell collapsed={collapsed} />
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {editNav ? (
            <>
              <div className="app-sidebar-text px-4 pb-2 text-[11px] leading-snug opacity-80">Přetáhněte položky nebo použijte šipky. Pořadí se uloží jen vám.</div>
              {orderedNav.map(({ to, label, icon: Icon }, idx) => (
                <div key={to} draggable
                     onDragStart={() => setDragNav(to)}
                     onDragEnd={() => setDragNav(null)}
                     onDragOver={e => { if (dragNav) e.preventDefault(); }}
                     onDrop={e => { e.preventDefault(); if (dragNav && dragNav !== to) moveNav(dragNav, idx); setDragNav(null); }}
                     className={`app-sidebar-text flex items-center gap-2 mx-2 px-2 py-2 rounded-lg mb-0.5 text-sm font-medium border border-dashed app-sidebar-border cursor-grab active:cursor-grabbing ${dragNav === to ? 'opacity-40' : ''}`}>
                  <GripVertical className="h-4 w-4 shrink-0 opacity-60" />
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate flex-1">{label}</span>
                  <button onClick={() => moveNav(to, idx - 1)} disabled={idx === 0} title="Posunout nahoru" className="app-sidebar-hover p-0.5 rounded disabled:opacity-25"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => moveNav(to, idx + 1)} disabled={idx === orderedNav.length - 1} title="Posunout dolů" className="app-sidebar-hover p-0.5 rounded disabled:opacity-25"><ChevronDown className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <button onClick={() => saveNavOrder([])} className="app-sidebar-text app-sidebar-hover mx-2 mt-2 px-2 py-1.5 rounded-lg text-xs flex items-center gap-1.5 w-[calc(100%-1rem)]">
                <RotateCcw className="h-3.5 w-3.5" /> Výchozí pořadí
              </button>
            </>
          ) : orderedNav.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `app-nav-item flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg mb-0.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'app-sidebar-active bg-blue-600 text-white'
                    : 'app-sidebar-text app-sidebar-hover text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}>
              <Icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span className="truncate">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Manuál + odhlášení (spodek panelu) */}
        <div className="border-t app-sidebar-border border-gray-700 p-3 space-y-1">
          <NavLink to="/manual" onClick={() => setMobileOpen(false)} title="Manuál a návody"
            className={({ isActive }) =>
              `app-sidebar-hover w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                collapsed ? 'justify-center' : ''} ${
                isActive ? 'app-sidebar-active bg-blue-600 text-white'
                         : 'app-sidebar-text text-gray-400 hover:bg-gray-800 hover:text-white'}`}>
            <BookOpen className="h-4 w-4 shrink-0" />
            {!collapsed && <span>Manuál a návody</span>}
          </NavLink>

          <button onClick={() => { setEditNav(v => !v); setCollapsed(false); }} title="Přeskládat položky menu"
                  className={`app-sidebar-hover w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${collapsed ? 'justify-center' : ''} ${editNav ? 'app-sidebar-active bg-blue-600 text-white' : 'app-sidebar-text text-gray-400 hover:bg-gray-800 hover:text-white'}`}>
            {editNav ? <Check className="h-4 w-4 shrink-0" /> : <Pencil className="h-4 w-4 shrink-0" />}
            {!collapsed && <span>{editNav ? 'Hotovo' : 'Upravit pořadí menu'}</span>}
          </button>

          {user && (
            <button onClick={handleLogout} title="Odhlásit se"
                    className={`app-sidebar-text app-sidebar-hover w-full flex items-center gap-2 px-3 py-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors text-sm ${
                      collapsed ? 'justify-center' : ''}`}>
              <LogOut className="h-4 w-4 shrink-0" />
              {!collapsed && <span>Odhlásit se</span>}
            </button>
          )}
        </div>

        {/* Collapse (desktop) */}
        <button onClick={() => setCollapsed(!collapsed)}
                className="app-sidebar-text app-sidebar-hover hidden lg:flex items-center justify-center h-10 border-t app-sidebar-border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="app-topbar h-16 bg-white border-b border-gray-200 flex items-center px-4 gap-4 shrink-0">
          <button className="lg:hidden p-2 rounded-lg hover:bg-gray-100" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <div className="flex-1">
            <h1 className="font-semibold text-gray-900 text-sm">
              {NAV_ITEMS.find(n => location.pathname.startsWith(n.to))?.label ?? (location.pathname.startsWith('/sales') ? 'Prodej' : 'CarFlow4u')}
            </h1>
          </div>

          {/* Branch switcher — viditelný jen pokud je addon aktivní */}
          {addons.multiBranch && (
            <BranchSwitcher userBranchId={user?.branchId ?? null} />
          )}

          {/* Světlý / tmavý režim (osobní, jen v tomto prohlížeči) */}
          <button onClick={uiMode.toggle} title={uiMode.dark ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'}
                  aria-label="Přepnout světlý / tmavý režim"
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors">
            {uiMode.dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          {/* User menu */}
          <div className="relative">
            <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                {initials}
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-gray-900 leading-tight">{user?.fullName ?? 'Admin'}</div>
                <div className="text-xs text-gray-400">{ROLE_LABELS[user?.role ?? ''] ?? user?.role}</div>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-gray-400 hidden md:block" />
            </button>

            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <div className="text-sm font-medium text-gray-900">{user?.fullName}</div>
                    <div className="text-xs text-gray-400">{user?.email}</div>
                  </div>
                  {addons.esign && sigState?.mode === 'USER' && sigState.canUse && (
                    <button onClick={() => { setUserMenuOpen(false); setMySigOpen(true); }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                      <PenLine className="h-4 w-4 text-gray-400" /> Můj podpis
                    </button>
                  )}
                  <button onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <Settings className="h-4 w-4 text-gray-400" /> Nastavení
                  </button>
                  <button onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                    <LogOut className="h-4 w-4" /> Odhlásit se
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {mySigOpen && <MySignatureModal onClose={() => setMySigOpen(false)} />}
        <main className="app-main flex-1 overflow-y-auto p-6">{children}</main>
      </div>
      </div>
    </div>
  );
}
